import React from "react";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import { utcToZonedTime } from "date-fns-tz";
import { EnforcementIntentDetail } from "common/graphql";
import { convertTimeToString } from "common/format";
import { resolveLiveSessionState } from "./liveSessionState";

/**
 * The "still in violation / tow eligible" badge (AUT-5502). Three states,
 * approved by the client 2026-08-27:
 *
 *   1. Session ended (or no live signal) → no badge. The flag must actively
 *      disappear once the vehicle leaves — never assert presence we can't see.
 *   2. Session open ≤ 2h → "Still in violation · as of HH:MM", stamped with
 *      the last data refresh so a tow is never dispatched on silently stale
 *      data.
 *   3. Session open > 2h → amber "Unverified — session open Nh": longer than
 *      almost any legitimate stay, so most likely a session the pipeline
 *      failed to close (CV-2333) rather than a vehicle still present.
 */
export const LiveSessionBadge: React.FC<LiveSessionBadgeProps> = ({ intent, asOf }) => {
  // No asOf yet means no completed network fetch — a cached notice must not
  // wear a badge stamped with a time we haven't actually confirmed.
  if (!intent || !asOf || intent.voidedAt) {
    return null;
  }

  const state = resolveLiveSessionState(intent.sessions, asOf.getTime());
  if (state.kind === "none") {
    return null;
  }

  const asOfLabel = convertTimeToString(utcToZonedTime(asOf, intent.timezone));

  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      {state.kind === "fresh" ? (
        <Chip color="error" size="small" label={`Still in violation · as of ${asOfLabel}`} />
      ) : (
        <Chip color="warning" size="small" label={`Unverified — session open ${state.openHours}h`} />
      )}
    </Box>
  );
};

export interface LiveSessionBadgeProps {
  intent?: EnforcementIntentDetail;
  /** When the intent data was last confirmed over the network — the badge's
   * honesty stamp. null until the first network response completes. */
  asOf: Date | null;
}

export default LiveSessionBadge;
